
import { useEffect, useRef, useState } from 'react';

export default function Home() {
  const [scrollY, setScrollY] = useState(0);
  const parallaxRefs = useRef<(HTMLDivElement | null)[]>([]);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    {
      id: 'email',
      title: 'Email Solutions',
      subtitle: 'Microsoft 365 & Google Workspace',
      description: 'Enterprise-grade email solutions with advanced security, collaboration features, and seamless integration across all your business applications.',
      features: ['Advanced Security', 'Cloud Storage', 'Real-time Collaboration', 'Mobile Access'],
      bgImage: 'https://readdy.ai/api/search-image?query=Futuristic%20holographic%20email%20interface%20with%20floating%20digital%20envelopes%20and%20communication%20networks%20in%20a%20high-tech%20blue%20and%20purple%20gradient%20background%20with%20glowing%20particles%20and%20data%20streams%20flowing%20through%20space%20creating%20a%20professional%20modern%20workspace%20atmosphere&width=1920&height=4800&seq=email-bg&orientation=portrait',
      color: 'from-blue-600 to-purple-600'
    },
    {
      id: 'voip',
      title: 'VoIP Telephony Solutions',
      subtitle: 'Advanced Communication Systems',
      description: 'Crystal-clear voice communications with advanced features like call routing, voicemail-to-email, and seamless integration with your business applications.',
      features: ['HD Voice Quality', 'Call Analytics', 'Multi-device Support', 'Auto Attendant'],
      bgImage: 'https://readdy.ai/api/search-image?query=Abstract%203D%20visualization%20of%20voice%20waves%20and%20telecommunication%20networks%20with%20floating%20geometric%20shapes%20and%20glowing%20connections%20in%20a%20dark%20tech%20environment%20with%20cyan%20and%20green%20accents%20representing%20modern%20VoIP%20technology%20and%20digital%20communication%20infrastructure&width=1920&height=4800&seq=voip-bg&orientation=portrait',
      color: 'from-green-600 to-cyan-600'
    },
    {
      id: 'video',
      title: 'Video Conferencing Systems',
      subtitle: 'Zoom, Teams & Advanced Platforms',
      description: 'High-definition video conferencing solutions that bring teams together with features like screen sharing, recording, and virtual backgrounds.',
      features: ['4K Video Quality', 'Screen Sharing', 'Virtual Backgrounds', 'Cloud Recording'],
      bgImage: 'https://readdy.ai/api/search-image?query=Futuristic%20video%20conference%20setup%20with%20multiple%20holographic%20screens%20displaying%20participants%20in%20a%20sleek%20modern%20office%20environment%20with%20ambient%20lighting%20and%20high-tech%20interfaces%20floating%20in%20space%20with%20purple%20and%20orange%20gradient%20backgrounds&width=1920&height=4800&seq=video-bg&orientation=portrait',
      color: 'from-purple-600 to-pink-600'
    },
    {
      id: 'project',
      title: 'Project Management Tools',
      subtitle: 'Jira, Trello, Asana & More',
      description: 'Streamline your workflow with powerful project management tools that help teams collaborate, track progress, and deliver results on time.',
      features: ['Task Tracking', 'Team Collaboration', 'Progress Analytics', 'Custom Workflows'],
      bgImage: 'https://readdy.ai/api/search-image?query=Dynamic%203D%20project%20management%20visualization%20with%20floating%20task%20cards%20kanban%20boards%20and%20interconnected%20workflow%20diagrams%20in%20a%20high-tech%20environment%20with%20glowing%20data%20connections%20and%20modern%20UI%20elements%20in%20orange%20and%20yellow%20gradient%20backgrounds&width=1920&height=4800&seq=project-bg&orientation=portrait',
      color: 'from-orange-600 to-yellow-600'
    },
    {
      id: 'intranet',
      title: 'Intranet & Collaboration Platforms',
      subtitle: 'SharePoint & Enterprise Solutions',
      description: 'Centralized platforms that connect your entire organization with document management, team sites, and collaborative workspaces.',
      features: ['Document Management', 'Team Sites', 'Knowledge Sharing', 'Enterprise Search'],
      bgImage: 'https://readdy.ai/api/search-image?query=Advanced%20corporate%20intranet%20visualization%20with%20interconnected%20nodes%20representing%20different%20departments%20and%20teams%20with%20flowing%20data%20streams%20and%20collaborative%20spaces%20in%20a%20professional%20dark%20blue%20and%20teal%20gradient%20environment%20with%20geometric%20patterns&width=1920&height=4800&seq=intranet-bg&orientation=portrait',
      color: 'from-teal-600 to-blue-600'
    }
  ];

  return (
    <div className="relative min-h-screen bg-gray-900 overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center text-white">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=Ultra-modern%20high-tech%20communication%20hub%20with%20holographic%20interfaces%20floating%20digital%20networks%20and%20futuristic%20architecture%20in%20deep%20space%20with%20glowing%20particles%20and%20energy%20streams%20creating%20an%20advanced%20technological%20atmosphere%20with%20blue%20purple%20gradient&width=1920&height=1080&seq=hero-bg&orientation=landscape')`,
            transform: `translateY(${scrollY * 0.5}px)`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70" />
        
        <div className="relative z-10 text-center max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-4 sm:mb-6 lg:mb-8 bg-gradient-to-r from-blue-400 via-purple-500 to-cyan-400 bg-clip-text text-transparent animate-pulse">
            Communication & Collaboration
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl text-gray-300 mb-8 sm:mb-12 lg:mb-16 leading-relaxed">
            Advanced solutions that transform how your team connects, collaborates, and achieves success
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-8">
            {sections.map((section, index) => (
              <div 
                key={section.id}
                className="group relative p-3 sm:p-4 lg:p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:bg-white/10 hover:border-white/20 transition-all duration-500 hover:scale-105 hover:rotate-1 cursor-pointer"
                onClick={() => document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' })}
              >
                <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-xl bg-gradient-to-r ${section.color} flex items-center justify-center mb-2 sm:mb-3 lg:mb-4 mx-auto group-hover:rotate-12 transition-transform duration-300`}>
                  <i className="ri-computer-line text-white text-sm sm:text-base lg:text-lg"></i>
                </div>
                <h3 className="text-xs sm:text-sm lg:text-base font-semibold text-center">{section.title}</h3>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-2xl sm:text-3xl lg:text-4xl"></i>
        </div>
      </section>

      {/* Sections */}
      {sections.map((section, index) => (
        <section
          key={section.id}
          id={section.id}
          ref={el => sectionRefs.current[index] = el}
          className="relative min-h-screen flex items-center py-16 sm:py-20 lg:py-24"
        >
          {/* Parallax Background */}
          <div
            ref={el => parallaxRefs.current[index] = el}
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('${section.bgImage}')`,
              transform: `translateY(${scrollY * (index % 2 === 0 ? -0.3 : 0.3)}px)`
            }}
          />
          <div className={`absolute inset-0 bg-gradient-to-r ${section.color} opacity-80`} />
          <div className="absolute inset-0 bg-black/40" />

          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
              {/* Content */}
              <div className={`${index % 2 === 1 ? 'lg:col-start-2' : ''} space-y-6 sm:space-y-8`}>
                <div className="space-y-4 sm:space-y-6">
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight">
                    {section.title}
                  </h2>
                  <h3 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl text-blue-200 font-medium">
                    {section.subtitle}
                  </h3>
                  <p className="text-base sm:text-lg lg:text-xl text-gray-300 leading-relaxed">
                    {section.description}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {section.features.map((feature, featureIndex) => (
                    <div
                      key={featureIndex}
                      className="group flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-pointer"
                    >
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-r ${section.color} flex items-center justify-center group-hover:rotate-12 transition-transform duration-300`}>
                        <i className="ri-check-line text-white text-sm sm:text-base"></i>
                      </div>
                      <span className="text-white font-medium text-sm sm:text-base">{feature}</span>
                    </div>
                  ))}
                </div>

                <button className={`group relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r ${section.color} text-white font-semibold rounded-xl hover:scale-105 hover:shadow-2xl transition-all duration-300 overflow-hidden`}>
                  <span className="relative z-10 flex items-center space-x-2 text-sm sm:text-base">
                    <span>Learn More</span>
                    <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300"></i>
                  </span>
                  <div className="absolute inset-0 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                </button>
              </div>

              {/* 3D Model Container */}
              <div className={`${index % 2 === 1 ? 'lg:col-start-1' : ''} relative`}>
                <div className="relative w-full h-64 sm:h-80 lg:h-96 xl:h-[32rem] bg-gradient-to-br from-white/10 to-transparent backdrop-blur-sm border border-white/20 rounded-3xl overflow-hidden group hover:scale-105 transition-all duration-500">
                  {/* 3D Model Placeholder with Animation */}
                  <div className="absolute inset-4 sm:inset-6 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl flex items-center justify-center group-hover:rotate-3 transition-all duration-700">
                    <div className="relative">
                      <div className={`w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 xl:w-32 xl:h-32 bg-gradient-to-r ${section.color} rounded-2xl flex items-center justify-center animate-spin group-hover:animate-pulse`}>
                        <i className={`${getIconForSection(section.id)} text-white text-2xl sm:text-3xl lg:text-4xl xl:text-5xl`}></i>
                      </div>
                      {/* Floating Elements */}
                      {[...Array(6)].map((_, i) => (
                        <div
                          key={i}
                          className={`absolute w-2 h-2 sm:w-3 sm:h-3 bg-white/40 rounded-full animate-ping`}
                          style={{
                            top: `${Math.sin(i * Math.PI / 3) * 60 + 50}%`,
                            left: `${Math.cos(i * Math.PI / 3) * 60 + 50}%`,
                            animationDelay: `${i * 0.2}s`
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Interactive Grid */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -skew-x-12 group-hover:skew-x-0 transition-transform duration-700"></div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Final CTA Section */}
      <section className="relative py-16 sm:py-20 lg:py-24 bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 sm:mb-8">
            Ready to Transform Your Communication?
          </h2>
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 mb-8 sm:mb-12 max-w-4xl mx-auto">
            Join thousands of organizations that have revolutionized their collaboration with our advanced solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center">
            <button className="group px-8 sm:px-10 py-4 sm:py-5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:scale-105 hover:shadow-2xl transition-all duration-300 text-base sm:text-lg">
              <span className="flex items-center space-x-2">
                <span>Get Started Today</span>
                <i className="ri-rocket-line group-hover:translate-x-2 transition-transform duration-300"></i>
              </span>
            </button>
            <button className="group px-8 sm:px-10 py-4 sm:py-5 border-2 border-white/30 text-white font-semibold rounded-xl hover:bg-white/10 hover:scale-105 transition-all duration-300 text-base sm:text-lg">
              <span className="flex items-center space-x-2">
                <span>Schedule Demo</span>
                <i className="ri-calendar-line group-hover:translate-x-2 transition-transform duration-300"></i>
              </span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function getIconForSection(sectionId: string): string {
  const icons = {
    email: 'ri-mail-line',
    voip: 'ri-phone-line',
    video: 'ri-video-line',
    project: 'ri-task-line',
    intranet: 'ri-global-line'
  };
  return icons[sectionId as keyof typeof icons] || 'ri-computer-line';
}
